// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import { deadlineFetch } from '../hooks/customEndpointFetch'

/**
 * How far behind the watermark every alerts-since poll re-reads (AlertPoll.tla F3).
 *
 * `lastCheck` advances to the server's clock (`lastUpdate`, taken on the Node
 * host AFTER its queries run), but an alert's `triggeredAt` is the DB's NOW() at
 * INSERT and the row becomes visible only at COMMIT. A row that becomes visible
 * just after a read, stamped at or before that read's `lastUpdate`, would never be
 * returned again. Re-reading a margin closes that; `mergeAlertsById` dedups the
 * overlap, and callers treat only ids NOT already listed as new.
 *
 * Measured 2026-09-24: RDS vs web1/web2 clock offset < 100 ms, engine INSERT is
 * autocommit, getAlertsSince runs well under a second — 2 min is >100x headroom
 * and costs a handful of re-read rows per poll.
 */
export const ALERT_POLL_OVERLAP_MS = 2 * 60 * 1000

/** The `lastCheck` query value for a poll whose watermark is `lastCheck`. */
export const alertsSinceParam = (lastCheck: Date): string =>
  new Date(lastCheck.getTime() - ALERT_POLL_OVERLAP_MS).toISOString()

/** Most pages one poll will walk (100 rows each) before giving up for this cycle. */
export const ALERTS_SINCE_MAX_PAGES = 20

type RawAlert = { id: number | string; triggeredAt?: string; triggered_at?: string }

export type AlertsSinceWalk =
  | {
      ok: true
      /** Every row the walk saw, deduped by id, NEWEST first (the merge's order). */
      alerts: RawAlert[]
      /**
       * The FIRST page's server time — the only safe watermark. The next poll
       * re-reads ALERT_POLL_OVERLAP_MS behind it, and that margin is sized for
       * one query: a row that commits just after the cursor passed its
       * triggeredAt is skipped by this walk, and only a watermark taken when
       * the walk STARTED keeps it inside the next poll's reach however long
       * the later pages took. The re-read rows are deduped by the merge.
       */
      lastUpdate?: string
      summary?: { unacknowledged?: number }
      /**
       * false when the walk stopped early (page cap, or a page added no new id).
       * The caller must then NOT advance its watermark: the next poll re-reads.
       */
      complete: boolean
    }
  | { ok: false; response: Response }

const triggeredMs = (a: RawAlert) => Date.parse(a.triggeredAt ?? a.triggered_at ?? '') || 0

/** The batch in the merge's order: newest first, id breaking ties. */
const newestFirst = (byId: Map<RawAlert['id'], RawAlert>): RawAlert[] =>
  Array.from(byId.values()).sort(
    (a, b) => triggeredMs(b) - triggeredMs(a) || (b.id > a.id ? 1 : b.id < a.id ? -1 : 0),
  )

/**
 * Read everything alerts-since has after `lastCheck`, page by page
 * (ada-core#230, AlertPoll.tla F4). The legacy endpoint returned only the
 * NEWEST 100 rows across ALL of the vet's pets, so in a busy window the older
 * rows — possibly the selected patient's — were never fetched once the
 * watermark moved. `paged=1` returns rows oldest-first with `hasMore` and a
 * `nextCursor`; the watermark may move only after the last page, and only to
 * the FIRST page's server time.
 *
 * Against a server without paging the response has no `hasMore`, so this is
 * exactly one legacy request — either deploy order is safe.
 */
export async function fetchAlertsSince(
  apiUrl: string,
  lastCheck: Date,
  init: RequestInit,
): Promise<AlertsSinceWalk> {
  const base = `${apiUrl}/alert-events/alerts-since?lastCheck=${alertsSinceParam(lastCheck)}&paged=1`
  const byId = new Map<RawAlert['id'], RawAlert>()
  let url = base
  let firstLastUpdate: string | undefined
  for (let page = 0; page < ALERTS_SINCE_MAX_PAGES; page++) {
    const response = await deadlineFetch(url, init)
    if (!response.ok) return { ok: false, response }
    const body = await response.json()
    const data = body?.data ?? {}
    if (page === 0) firstLastUpdate = data.lastUpdate
    const rows: RawAlert[] = body?.success ? (data.alerts ?? []) : []
    const before = byId.size
    // A continuation re-returns its cursor row (µs vs ms); the later copy wins.
    for (const row of rows) byId.set(row.id, row)
    const alerts = newestFirst(byId)
    const cursor = data.nextCursor
    if (!body?.success || data.hasMore !== true || !cursor) {
      return {
        ok: true,
        alerts,
        lastUpdate: firstLastUpdate,
        summary: data.summary,
        complete: true,
      }
    }
    if (page > 0 && byId.size === before) {
      return { ok: true, alerts, lastUpdate: undefined, summary: data.summary, complete: false }
    }
    url = `${base}&afterTs=${encodeURIComponent(cursor.triggeredAt)}&afterId=${encodeURIComponent(cursor.id)}`
  }
  return {
    ok: true,
    alerts: newestFirst(byId),
    lastUpdate: undefined,
    summary: undefined,
    complete: false,
  }
}
